import React, { useEffect, useRef, useState } from "react";
import { Check, ShoppingBag, MessageCircle, Code2, Database, Globe, ArrowRight, Phone, Bot } from "lucide-react";
import { useT } from "../context/LanguageContext";

const SLIDES = [
  { src: "/images/pages/ecommerce-slide.jpg", alt: "E-ticaret asistanı", label: "E-Ticaret" },
  { src: "/images/pages/social-slide.jpg", alt: "WhatsApp ve sosyal medya", label: "WhatsApp & Sosyal Medya" },
  { src: "/images/pages/chatbot-slide.jpg", alt: "Chatbot mesajlaşma", label: "Chat Bot" },
  { src: "/images/pages/embed-slide.jpg", alt: "Kendi uygulamanıza gömün", label: "Kendi Uygulamanıza Gömün" },
  { src: "/images/pages/crm-slide.jpg", alt: "CRM entegrasyonu", label: "CRM" },
  { src: "/images/pages/telephony-slide.jpg", alt: "Telefon entegrasyonu", label: "Telefon" },
];

const HumanVoiceSection = () => {
  const t = useT();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % SLIDES.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [visible]);

  const features = [
    { icon: ShoppingBag, title: "E-Ticaret Asistanı", desc: "Sipariş takibi, stok sorgulama ve iade işlemlerini sesli veya yazılı olarak yönetin. Trendyol, Shopify entegrasyonu.", color: "#FF7A7A", slide: 0 },
    { icon: MessageCircle, title: "WhatsApp & Sosyal Medya", desc: "WhatsApp, Instagram ve Telegram'dan gelen mesajları AI ile yanıtlayın. Tek panelden tüm kanalları yönetin.", color: "#14B8A6", slide: 1 },
    { icon: Bot, title: "Chat Bot", desc: "Web sitenize veya uygulamanıza akıllı chatbot ekleyin. Doğal dil işleme ile müşteri sorularını anlayıp anında yanıt verin.", color: "#4B6FD4", slide: 2 },
    { icon: Code2, title: "Kendi Uygulamanıza Gömün", desc: "SDK ve REST API ile kendi web veya mobil uygulamanıza entegre edin. Tam özelleştirme, markanıza özel tasarım.", color: "#8B5CF6", slide: 3 },
    { icon: Database, title: "CRM Entegrasyonu", desc: "HubSpot, Dynamics 365, Zendesk ile çift yönlü senkronizasyon. Her görüşme CRM'inize otomatik kaydedilir.", color: "#7C3AED", slide: 4 },
    { icon: Phone, title: "Telefon Entegrasyonu", desc: "NetGSM, Verimor, Twilio ile mevcut numaranızı taşımadan entegre olun. SIP trunk, sanal santral, çağrı yönlendirme.", color: "#F59E0B", slide: 5 },
  ];

  return (
    <section
      id="integrations"
      ref={ref}
      className={`section-fade ${visible ? "visible" : ""} py-16 px-6 flex items-center`}
      style={{ minHeight: "100vh", background: "var(--section-surface-4)" }}
    >
      <div className="max-w-6xl mx-auto w-full">
        <div className="mb-14 flex flex-col items-center text-center">
          <h2 className="font-bold tracking-tight" style={{ color: "var(--fg)", fontSize: "clamp(2rem, 4.2vw, 3.25rem)" }}>
            <span style={{ color: "var(--brand-coral)" }}>NetGSM</span>,{" "}
            <span style={{ color: "var(--brand-cyan)" }}>Verimor</span>{" "}
            {t("integrations.titleTail")}
          </h2>
          <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--fg)", opacity: 0.92 }}>
            {t("integrations.subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
        {/* Left: Image carousel */}
        <div className="relative">
          <div
            className="relative rounded-3xl overflow-hidden"
            style={{ border: "1px solid var(--border)", aspectRatio: "1/1" }}
          >
            {SLIDES.map((slide, i) => (
              <img
                key={slide.src}
                src={slide.src}
                alt={slide.alt}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: i === activeSlide ? 1 : 0 }}
                loading="lazy"
              />
            ))}
            {/* Slide label */}
            <div
              className="absolute bottom-4 left-4 px-4 py-2 rounded-full text-[12px] font-semibold"
              style={{
                background: "rgba(15,21,48,0.85)",
                color: "#ffffff",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              {SLIDES[activeSlide].label}
            </div>
          </div>

          {/* Active feature description above dots */}
          <div className="mt-4 text-center px-2">
            <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.85 }}>
              {features[activeSlide].desc}
            </p>
          </div>

          {/* Slide dots */}
          <div className="flex items-center justify-center gap-2 mt-3">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === activeSlide ? 24 : 8,
                  height: 8,
                  background: i === activeSlide ? "var(--brand-cyan)" : "var(--border)",
                  border: "none",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>

          {/* Slide counter */}
          <div className="text-center mt-2">
            <span className="text-[11px] font-medium" style={{ color: "var(--muted)" }}>
              {activeSlide + 1} / {SLIDES.length}
            </span>
          </div>
        </div>

        {/* Right: Features */}
        <div className="flex flex-col">
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
            style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--muted)" }}
          >
            {t("humanVoice.eyebrow")}
          </span>
          <h2
            className="font-bold tracking-tight leading-[1.1]"
            style={{ color: "var(--fg)", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}
          >
            Her kanalda, <span className="brand-gradient-text">her yerde</span>
          </h2>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.85 }}>
            E-ticaret, WhatsApp, sosyal medya, kendi uygulamanız veya CRM — sesli ve yazılı AI asistanınız tüm kanallarda tutarlı ve akıllı hizmet verir.
          </p>

          {/* Feature cards - icon + title only */}
          <div
            className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 flex-1"
            style={{ gridAutoRows: "1fr" }}
          >
            {features.map((f, index) => (
              <div
                key={f.title}
                className="group p-3 rounded-xl flex items-center gap-2.5 transition-all duration-200 hover:-translate-y-0.5 h-full"
                style={{
                  background: f.slide === activeSlide ? "rgba(75,111,212,0.08)" : "var(--card-bg)",
                  border: f.slide === activeSlide ? "1px solid var(--brand-cyan)" : "1px solid var(--border)",
                  cursor: "pointer",
                  boxShadow: f.slide === activeSlide ? "0 8px 24px rgba(75,111,212,0.08)" : "none",
                }}
                onMouseEnter={() => setActiveSlide(f.slide)}
                onClick={() => setActiveSlide(f.slide)}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${f.color}15`, color: f.color }}
                >
                  <f.icon size={16} />
                </div>
                <span className="text-[13px] font-semibold flex-1" style={{ color: "var(--fg)" }}>
                  {f.title}
                </span>
                <ArrowRight size={13} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: f.color }} />
              </div>
            ))}
          </div>

        </div>
        </div>
      </div>
    </section>
  );
};

export default HumanVoiceSection;
